import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { invoiceService, statusService } from '@/services';
import { CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { format, parseISO } from 'date-fns';

interface DashboardNewInvoicesCardProps {
  companyId: string;
}

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

export function DashboardNewInvoicesCard({ companyId }: DashboardNewInvoicesCardProps) {
  const navigate = useNavigate();
  const [selectedStatusId, setSelectedStatusId] = useState<string | null>(null);

  const { data: statuses } = useQuery({
    queryKey: ['dashboard-invoice-statuses', companyId],
    queryFn: async () => {
      const result = await statusService.listByEntityType('invoice', companyId);
      return result.data || [];
    },
    staleTime: 5 * 60 * 1000,
    enabled: !!companyId,
  });

  const { data: defaultStatus } = useQuery({
    queryKey: ['dashboard-invoice-default-status', companyId],
    queryFn: async () => {
      const result = await statusService.getDefault('invoice', companyId);
      return result.data;
    },
    staleTime: 5 * 60 * 1000,
    enabled: !!companyId,
  });

  const { data: invoices, isLoading } = useQuery({
    queryKey: ['dashboard-new-invoices', companyId],
    queryFn: async () => {
      const result = await invoiceService.listWithRelations(companyId);
      return result.data || [];
    },
    staleTime: 5 * 60 * 1000,
    enabled: !!companyId,
  });

  // Initialize selectedStatusId to default when it loads
  const activeStatusId = selectedStatusId ?? defaultStatus?.id ?? null;

  const filteredInvoices = (invoices || [])
    .filter((inv: any) => !activeStatusId || inv.status_id === activeStatusId)
    .sort((a: any, b: any) => {
      const dateA = a.invoice_date || '';
      const dateB = b.invoice_date || '';
      return dateB.localeCompare(dateA);
    })
    .slice(0, 20);

  return (
    <>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-2">
          <CardTitle>New Invoices</CardTitle>
          <Select
            value={activeStatusId || ''}
            onValueChange={(value) => setSelectedStatusId(value)}
          >
            <SelectTrigger className="w-[160px] h-8 text-sm">
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent>
              {(statuses || []).map((status: any) => (
                <SelectItem key={status.id} value={status.id}>
                  {status.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent className="p-0 flex-1 overflow-auto">
        {isLoading ? (
          <div className="p-4 space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-8 w-full" />
            ))}
          </div>
        ) : filteredInvoices.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Supplier</TableHead>
                <TableHead className="text-right">Total</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredInvoices.map((inv: any) => (
                <TableRow
                  key={inv.id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => navigate(`/invoices/${inv.id}`)}
                >
                  <TableCell className="text-sm">
                    <div>{inv.vendor?.name || 'Unknown'}</div>
                    <div className="text-xs text-muted-foreground">
                      {inv.invoice_date
                        ? format(parseISO(inv.invoice_date), 'MMM d, yyyy')
                        : '—'}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-right">
                    {inv.total_amount != null
                      ? currencyFormatter.format(inv.total_amount)
                      : '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <div className="h-full flex items-center justify-center text-muted-foreground p-6">
            No invoices found for this status
          </div>
        )}
      </CardContent>
    </>
  );
}
